import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import apiService from '../services/api';

const symbolsList = [
  { id: 'nifty', name: 'NIFTY' },
  { id: 'banknifty', name: 'BANKNIFTY' },
  { id: 'finnifty', name: 'FINNIFTY' },
];

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold mt-1">{value ?? '-'}</div>
    </div>
  );
}

function formatNumber(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  if (typeof n === 'string') return n;
  return Number(n).toFixed(digits);
}

function OptionChain() {
  const [symbol, setSymbol] = useState('nifty');
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState('15:30');
  const [expiries, setExpiries] = useState([]);
  const [expiry, setExpiry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [around, setAround] = useState(20);

  // Load defaults from settings
  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.settings.get();
        if (res?.data?.default_symbol) setSymbol(res.data.default_symbol);
      } catch {}
    })();
  }, []);

  // Load expiries when symbol changes
  useEffect(() => {
    (async () => {
      try {
        setExpiries([]);
        setExpiry('');
        const r = await apiService.optionChain.getExpiries(symbol);
        const list = r.data?.expiries || [];
        setExpiries(list);
        // pick nearest >= selected date, else last
        const d = new Date(date);
        const dStr = d.toISOString().slice(0, 10);
        const nearest = list.find(e => e >= dStr) || list[list.length - 1];
        if (nearest) setExpiry(nearest);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [symbol]);

  const fetchSnapshot = async () => {
    if (!expiry) return;
    setLoading(true);
    setError('');
    try {
      const payload = {
        symbol,
        expiry,
        date: date.toISOString().slice(0, 10),
        time,
        strikes_around: Number(around) || 20,
      };
      const r = await apiService.optionChain.getSnapshot(payload);
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // auto-load on ready
    if (expiry) fetchSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiry, date, time, around]);

  const onQuickTime = (deltaMinutes) => {
    // Adjust time by delta minutes and clamp to market window 09:15 - 15:30
    const [hh, mm] = time.split(':').map(Number);
    const base = new Date(2000, 0, 1, hh || 0, mm || 0, 0);
    base.setMinutes(base.getMinutes() + deltaMinutes);
    const minutesOfDay = base.getHours() * 60 + base.getMinutes();
    const minM = 9 * 60 + 15;  // 09:15
    const maxM = 15 * 60 + 30; // 15:30
    const clamped = Math.min(Math.max(minutesOfDay, minM), maxM);
    const nh = String(Math.floor(clamped / 60)).padStart(2, '0');
    const nm = String(clamped % 60).padStart(2, '0');
    setTime(`${nh}:${nm}`);
  };

  const onDayShift = (days) => {
    const nd = new Date(date);
    nd.setDate(nd.getDate() + days);
    setDate(nd);
  };

  // ----- Time dropdowns (no AM/PM) with constraints -----
  const allowedHours = ['09','10','11','12','13','14','15'];
  const minutesForHour = (hStr) => {
    const h = parseInt(hStr, 10);
    let start = 0, end = 59;
    if (h === 9) { start = 15; end = 59; }
    else if (h === 15) { start = 0; end = 30; }
    const arr = [];
    for (let m = start; m <= end; m++) arr.push(String(m).padStart(2, '0'));
    return arr;
  };

  const [hour, setHour] = useState(time.split(':')[0]);
  const [minute, setMinute] = useState(time.split(':')[1]);

  // keep hour/minute synced when time is changed programmatically
  useEffect(() => {
    const [h, m] = time.split(':');
    setHour(h);
    // Ensure minute respects constraints for that hour
    const allowed = minutesForHour(h);
    const mFixed = allowed.includes(m) ? m : allowed[0];
    setMinute(mFixed);
    if (mFixed !== m) setTime(`${h}:${mFixed}`);
  }, [time]);

  const onHourChange = (h) => {
    const allowed = minutesForHour(h);
    const m = allowed.includes(minute) ? minute : allowed[0];
    setHour(h);
    setMinute(m);
    setTime(`${h}:${m}`);
  };

  const onMinuteChange = (m) => {
    const allowed = minutesForHour(hour);
    const mFixed = allowed.includes(m) ? m : allowed[0];
    setMinute(mFixed);
    setTime(`${hour}:${mFixed}`);
  };

  const lots = useMemo(() => ({ nifty: 75, banknifty: 30, finnifty: 65 }), []);

  const headerStats = useMemo(() => ({
    spot: data?.spot_price ? formatNumber(data.spot_price, 2) : '-',
    fut: data?.futures_price ? formatNumber(data.futures_price, 2) : '-',
    time: data?.time || time,
    dte: data?.days_to_expiry ?? '-',
    lot: lots[symbol] || '-',
    pcr: data?.pcr ? formatNumber(data.pcr, 2) : '-',
  }), [data, time, symbol, lots]);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Option Chain Dashboard</h2>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Instrument</label>
            <select className="w-full border rounded px-2 py-2" value={symbol} onChange={e => setSymbol(e.target.value)}>
              {symbolsList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Expiry</label>
            <select className="w-full border rounded px-2 py-2" value={expiry} onChange={e => setExpiry(e.target.value)}>
              <option value="">Select</option>
              {expiries.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Date</label>
            <DatePicker
              selected={date}
              onChange={setDate}
              dateFormat="yyyy-MM-dd"
              className="w-full border rounded px-2 py-2"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Time</label>
            <div className="flex gap-2">
              <select
                value={hour}
                onChange={e => onHourChange(e.target.value)}
                className="w-24 border rounded px-2 py-2"
              >
                {allowedHours.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <span className="self-center">:</span>
              <select
                value={minute}
                onChange={e => onMinuteChange(e.target.value)}
                className="w-24 border rounded px-2 py-2"
              >
                {minutesForHour(hour).map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">ATM ± Strikes</label>
            <input type="number" min={1} max={50} value={around} onChange={e => setAround(e.target.value)} className="w-full border rounded px-2 py-2" />
          </div>
          <div className="flex items-end">
            <button onClick={fetchSnapshot} className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium rounded px-4 py-2">Load</button>
          </div>
        </div>

        {/* Quick time controls */}
        <div className="flex flex-wrap gap-2 mt-3">
          <button className="px-3 py-1 border rounded" onClick={() => onDayShift(-1)}>−1 DAY</button>
          <button className="px-3 py-1 border rounded" onClick={() => setTime('09:15')}>SOD</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(-60)}>1HR</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(-30)}>30MIN</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(-15)}>15MIN</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(-5)}>5MIN</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(5)}>5MIN</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(15)}>15MIN</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(30)}>30MIN</button>
          <button className="px-3 py-1 border rounded" onClick={() => onQuickTime(60)}>1HR</button>
          <button className="px-3 py-1 border rounded" onClick={() => setTime('15:30')}>EOD</button>
          <button className="px-3 py-1 border rounded" onClick={() => onDayShift(1)}>+1 DAY</button>
        </div>
      </div>

      {/* Header stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-4">
        <Stat label="Spot Price" value={headerStats.spot} />
        <Stat label="Time" value={headerStats.time} />
        <Stat label="Future Price" value={headerStats.fut} />
        <Stat label="Days for Expiry" value={headerStats.dte} />
        <Stat label="Market Lot" value={headerStats.lot} />
        <Stat label="PCR" value={headerStats.pcr} />
      </div>

      {/* Errors/Loading */}
      {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded p-3 mb-3">{error}</div>}
      {loading && <div className="text-gray-600 mb-3">Loading option chain...</div>}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-right">OI</th>
              <th className="px-3 py-2 text-right">IV</th>
              <th className="px-3 py-2 text-right">Gamma</th>
              <th className="px-3 py-2 text-right">Theta</th>
              <th className="px-3 py-2 text-right">Delta</th>
              <th className="px-3 py-2 text-right">LTP</th>
              <th className="px-3 py-2 text-center">Strike</th>
              <th className="px-3 py-2 text-left">LTP</th>
              <th className="px-3 py-2 text-left">Delta</th>
              <th className="px-3 py-2 text-left">Theta</th>
              <th className="px-3 py-2 text-left">Gamma</th>
              <th className="px-3 py-2 text-left">IV</th>
              <th className="px-3 py-2 text-left">OI</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows?.length ? (
              data.rows.map((r) => {
                const atm = data?.atm_strike;
                const isATM = atm && r.strike === atm;
                const nearATM = atm && Math.abs(r.strike - atm) <= (symbolsList.find(s=>s.id===symbol)?.id==='banknifty'?200:100);
                const rowClass = isATM
                  ? 'bg-yellow-50'
                  : nearATM
                  ? 'bg-gray-50'
                  : '';
                return (
                  <tr key={r.strike} className={rowClass}>
                    {/* CALL side */}
                    <td className="px-3 py-2 text-right">{formatNumber(r.call?.oi, 0)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(r.call?.iv, 2)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(r.call?.gamma, 4)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(r.call?.theta, 2)}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(r.call?.delta, 2)}</td>
                    <td className="px-3 py-2 text-right text-primary-700 font-medium">{formatNumber(r.call?.ltp, 2)}</td>

                    {/* STRIKE */}
                    <td className="px-3 py-2 text-center font-semibold">{r.strike}</td>

                    {/* PUT side */}
                    <td className="px-3 py-2 text-left text-primary-700 font-medium">{formatNumber(r.put?.ltp, 2)}</td>
                    <td className="px-3 py-2 text-left">{formatNumber(r.put?.delta, 2)}</td>
                    <td className="px-3 py-2 text-left">{formatNumber(r.put?.theta, 2)}</td>
                    <td className="px-3 py-2 text-left">{formatNumber(r.put?.gamma, 4)}</td>
                    <td className="px-3 py-2 text-left">{formatNumber(r.put?.iv, 2)}</td>
                    <td className="px-3 py-2 text-left">{formatNumber(r.put?.oi, 0)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-gray-500">No data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OptionChain;
